import type { RefObject } from 'react'
import { useCallback, useEffect, useState } from 'react'

interface ContextListMeasurements {
  canScrollUp: boolean
  canScrollDown: boolean
  onScroll: () => void
}

function getIntrinsicContextContentHeight(listElement: HTMLDivElement): number {
  const itemElements = Array.from(listElement.children) as HTMLElement[]
  if (itemElements.length === 0) {
    return 0
  }

  const computedStyles = window.getComputedStyle(listElement)
  const paddingTop = Number.parseFloat(computedStyles.paddingTop || '0') || 0
  const paddingBottom = Number.parseFloat(computedStyles.paddingBottom || '0') || 0
  const rowGap = Number.parseFloat(computedStyles.rowGap || computedStyles.gap || '0') || 0
  const itemsHeight = itemElements.reduce(
    (sum, item) => sum + item.getBoundingClientRect().height,
    0
  )
  const gapsHeight = Math.max(0, itemElements.length - 1) * rowGap
  const contentHeight = itemsHeight + gapsHeight
  return Math.ceil(Math.max(0, contentHeight + paddingTop + paddingBottom))
}

/**
 * Measures a context list's intrinsic content height (for ChatInput's sizing allocator) and
 * whether it can scroll further up/down (for the fade indicators). The list stays mounted while
 * collapsed - the frame around it is just clipped to 0px, so its items keep their real size and
 * this stays accurate the whole time. Measuring only while expanded meant the first render after
 * expanding had no number yet and the caller had to guess, then correct itself a render later.
 */
export function useContextListMeasurements(
  listRef: RefObject<HTMLDivElement | null>,
  itemCount: number,
  onContentHeightChange?: (height: number) => void
): ContextListMeasurements {
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  const checkScroll = useCallback(() => {
    if (!listRef.current) return
    const { scrollTop, scrollHeight, clientHeight } = listRef.current
    const nextUp = scrollTop > 0
    const nextDown = scrollTop + clientHeight < scrollHeight - 1

    setCanScrollUp((prev) => (prev !== nextUp ? nextUp : prev))
    setCanScrollDown((prev) => (prev !== nextDown ? nextDown : prev))
  }, [listRef])

  useEffect(() => {
    if (!listRef.current) return

    const reportHeight = () => {
      if (!listRef.current) return
      // Intrinsic content height (rendered context items + list paddings) - the caller folds
      // this into contextDemand so the list fits its content instead of the viewport-based cap
      // when there's less content than that cap allows.
      onContentHeightChange?.(getIntrinsicContextContentHeight(listRef.current))
      checkScroll()
    }

    reportHeight()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      reportHeight()
    })

    observer.observe(listRef.current)
    for (const child of Array.from(listRef.current.children)) {
      observer.observe(child)
    }

    let mutationObserver: MutationObserver | null = null
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        reportHeight()
        if (!listRef.current) return
        for (const child of Array.from(listRef.current.children)) {
          observer.observe(child)
        }
      })

      mutationObserver.observe(listRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    }

    return () => {
      mutationObserver?.disconnect()
      observer.disconnect()
    }
  }, [listRef, onContentHeightChange, itemCount, checkScroll])

  return { canScrollUp, canScrollDown, onScroll: checkScroll }
}
